import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Sub-section config ── */
interface SubSection {
  key: string;
  label: string;
  endpoint: string;
}

const SUB_SECTIONS: SubSection[] = [
  { key: 'rolling', label: 'Rolling', endpoint: '/api/rolling-techniques' },
  { key: 'recipes', label: 'Recipes', endpoint: '/api/cannabis-recipes' },
  { key: 'growing', label: 'Growing', endpoint: '/api/growing-guides' },
  { key: 'medical', label: 'Medical', endpoint: '/api/medical-guide' },
  { key: 'pairings', label: 'Pairings', endpoint: '/api/strain-pairings' },
  { key: 'prices', label: 'Prices', endpoint: '/api/price-index' },
  { key: 'terpenes', label: 'Terpenes', endpoint: '/api/terpenes' },
  { key: 'methods', label: 'Methods', endpoint: '/api/consumption-methods' },
  { key: 'festivals', label: 'Festivals', endpoint: '/api/festivals' },
  { key: 'podcasts', label: 'Podcasts', endpoint: '/api/podcasts-channels' },
  { key: 'myths', label: 'Myths', endpoint: '/api/myth-busters' },
  { key: 'drugtests', label: 'Drug Tests', endpoint: '/api/drug-test-info' },
  { key: 'history', label: 'History', endpoint: '/api/cannabis-history' },
];

/* ── Generic item type ── */
interface GuideItem {
  [key: string]: unknown;
  name?: string;
  title?: string;
  description?: string;
  content?: string;
  image_url?: string;
  url?: string;
  link?: string;
  difficulty?: string;
  duration?: string;
  category?: string;
  type?: string;
  country?: string;
  date?: string;
  price?: string;
  rating?: number;
  steps?: string[];
  ingredients?: string[];
  benefits?: string[];
  effects?: string[];
  flavor?: string;
  aroma?: string;
  myth?: string;
  fact?: string;
  question?: string;
  answer?: string;
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

/* ── Card renderer for any guide item ── */
function s(val: unknown): string { return val != null ? String(val) : ''; }

function GuideCard({ item, sectionKey }: { item: GuideItem; sectionKey: string }) {
  const r = item as Record<string, unknown>;
  // Pre-extract all fields as strings
  const title = s(r.name || r.title || r.test_type || r.event || r.myth || r.condition || r.activity || r.question || r.city || 'Untitled');
  const desc = s(r.description || r.content || '');
  const year = typeof r.year === 'number' ? r.year : undefined;
  const truth = s(r.truth);
  const explanation = s(r.explanation);
  const detectionWindow = s(r.detection_window);
  const cannabinoids = s(r.recommended_cannabinoids);
  const dosage = s(r.dosage_notes);
  const priceUsd = s(r.avg_price_gram_usd);
  const city = s(r.city);
  const country = s(r.country);
  const qualityNotes = s(r.quality_notes);
  const why = s(r.why);
  const dates = s(r.dates);
  const platform = s(r.platform);
  const hosts = s(r.hosts);
  const link = item.url || item.link;

  return (
    <motion.div
      variants={staggerItem}
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/20 transition-colors"
    >
      {item.image_url && (
        <img src={item.image_url} alt={title} className="w-full h-36 object-cover rounded-xl mb-3" />
      )}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-white font-semibold text-sm">
            {year != null && <span className="text-[#d4a574] mr-1.5">{year > 0 ? year : `${Math.abs(year)} BCE`}</span>}
            {title}
          </h3>
          {(item.difficulty || item.category || item.type) && (
            <span className="shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#d4a574]/10 text-[#d4a574] border border-[#d4a574]/20">
              {item.difficulty || item.category || item.type}
            </span>
          )}
        </div>

        {/* Myth */}
        {sectionKey === 'myths' && (
          <div className="space-y-1.5">
            {truth && <p className="text-[#4ade80]/80 text-xs"><span className="font-semibold">Truth:</span> {truth}</p>}
            {explanation && <p className="text-white/50 text-xs line-clamp-3">{explanation}</p>}
          </div>
        )}

        {/* Drug tests */}
        {sectionKey === 'drugtests' && (
          <div className="space-y-1.5">
            {detectionWindow && <p className="text-[#d4a574] text-xs">{detectionWindow}</p>}
            {desc && <p className="text-white/50 text-xs line-clamp-3">{desc}</p>}
          </div>
        )}

        {/* History */}
        {sectionKey === 'history' && desc && (
          <p className="text-white/50 text-xs line-clamp-3 leading-relaxed">{desc}</p>
        )}

        {/* Medical */}
        {sectionKey === 'medical' && (
          <div className="space-y-1.5">
            {desc && <p className="text-white/50 text-xs line-clamp-2">{desc}</p>}
            {cannabinoids && <p className="text-[#4ade80]/70 text-xs">Cannabinoids: {cannabinoids}</p>}
            {dosage && <p className="text-white/40 text-xs">Dosage: {dosage}</p>}
          </div>
        )}

        {/* Prices */}
        {sectionKey === 'prices' && (
          <div className="space-y-1">
            {priceUsd && <p className="text-[#4ade80] text-sm font-bold">${priceUsd}/gram</p>}
            {city && <p className="text-white/50 text-xs">{city}{country ? `, ${country}` : ''}</p>}
            {qualityNotes && <p className="text-white/30 text-xs">{qualityNotes}</p>}
          </div>
        )}

        {/* Pairings */}
        {sectionKey === 'pairings' && (
          <div className="space-y-1.5">
            {desc && <p className="text-white/50 text-xs">{desc}</p>}
            {why && <p className="text-white/40 text-xs italic">{why}</p>}
          </div>
        )}

        {/* Festivals */}
        {sectionKey === 'festivals' && (
          <div className="space-y-1">
            {desc && <p className="text-white/50 text-xs line-clamp-2">{desc}</p>}
            {dates && <p className="text-[#d4a574] text-xs">{dates}</p>}
            {(city || country) && <p className="text-white/40 text-xs">{[city, country].filter(Boolean).join(', ')}</p>}
          </div>
        )}

        {/* Podcasts */}
        {sectionKey === 'podcasts' && (
          <div className="space-y-1">
            {desc && <p className="text-white/50 text-xs line-clamp-2">{desc}</p>}
            {platform && <p className="text-[#d4a574] text-xs">{platform}</p>}
            {hosts && <p className="text-white/40 text-xs">Hosted by: {hosts}</p>}
          </div>
        )}

        {/* General — for sections not handled above */}
        {!['myths','drugtests','history','medical','prices','pairings','festivals','podcasts'].includes(sectionKey) && desc && (
          <p className="text-white/50 text-xs line-clamp-3 leading-relaxed">{desc}</p>
        )}

        {/* Meta row */}
        <div className="flex flex-wrap gap-2 text-[10px]">
          {item.duration && (
            <span className="text-white/40">{item.duration}</span>
          )}
          {item.country && (
            <span className="text-white/40">{item.country}</span>
          )}
          {item.date && (
            <span className="text-white/30">{item.date}</span>
          )}
          {item.price && (
            <span className="text-[#4ade80]">{item.price}</span>
          )}
          {item.flavor && (
            <span className="text-[#d4a574]">{item.flavor}</span>
          )}
          {item.aroma && (
            <span className="text-purple-300">{item.aroma}</span>
          )}
        </div>

        {/* List fields */}
        {item.effects && item.effects.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.effects.map((e, i) => (
              <span key={i} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#4ade80]/10 text-[#4ade80]/80">{e}</span>
            ))}
          </div>
        )}
        {item.benefits && item.benefits.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.benefits.map((b, i) => (
              <span key={i} className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-500/10 text-blue-300">{b}</span>
            ))}
          </div>
        )}
        {item.ingredients && item.ingredients.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.ingredients.map((ing, i) => (
              <span key={i} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#d4a574]/10 text-[#d4a574]/80">{ing}</span>
            ))}
          </div>
        )}
        {item.steps && item.steps.length > 0 && (
          <ol className="text-white/50 text-xs space-y-1 list-decimal list-inside mt-2">
            {item.steps.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        )}

        {/* External link */}
        {link && (
          <a
            href={String(link)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-1 text-[#4ade80] text-xs hover:underline"
          >
            Learn more &rarr;
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function GuidesTab(_props: CountryProps) {
  const [activeSection, setActiveSection] = useState('rolling');
  const [data, setData] = useState<Record<string, GuideItem[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Lazy fetch only active section
  useEffect(() => {
    if (data[activeSection] || loading[activeSection]) return;

    const section = SUB_SECTIONS.find(s => s.key === activeSection);
    if (!section) return;

    setLoading(prev => ({ ...prev, [activeSection]: true }));

    fetch(getApiUrl(section.endpoint))
      .then(res => {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then(result => {
        const items = Array.isArray(result) ? result : result.data ?? [];
        setData(prev => ({ ...prev, [activeSection]: items }));
      })
      .catch(() => {
        setData(prev => ({ ...prev, [activeSection]: [] }));
      })
      .finally(() => {
        setLoading(prev => ({ ...prev, [activeSection]: false }));
      });
  }, [activeSection, data, loading]);

  const items = data[activeSection] ?? [];
  const isLoading = loading[activeSection] ?? false;

  return (
    <div className="space-y-6">
      {/* Sub-navigation pills */}
      <div className="flex gap-2 flex-wrap">
        {SUB_SECTIONS.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-colors ${
              activeSection === s.key
                ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]'
                : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20 hover:text-white/70'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <p>No content available for this section yet.</p>
        </div>
      ) : (
        <motion.div
          key={activeSection}
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {items.map((item, i) => (
            <GuideCard key={i} item={item} sectionKey={activeSection} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
